import React, { useContext, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Avatar,
  IconButton,
  useMediaQuery,
  useTheme,
  // Typography,
  Alert,
  Box,
  CircularProgress,
} from '@mui/material';
import { FaTimes, FaUser, FaCamera } from 'react-icons/fa';
import { AuthContext } from '../../auth/AuthContext';
import { uploadMedia } from '../../utils/MediaUploader';
import { showSwalNotification } from '../../utils/SwalResponse';

const defaultProfile =
  'https://cdn-icons-png.flaticon.com/512/149/149071.png';

const UpdateProfile = ({ open, onClose, user }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {updateProfile} = useContext(AuthContext)

  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    organisation: user?.organisation || '',
    profile_picture: user?.profile || '',
  });
  const [profilePreview, setProfilePreview] = useState(user?.profile_picture || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Handle input changes
  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Handle profile picture upload
  const handleProfilePic = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file?.type?.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePreview(reader.result);
      setForm((prev) => ({
        ...prev,
        profile_picture: file,
      }));
    };
    reader.readAsDataURL(file);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      let updatedForm = { ...form };

      // If profile_picture is a File, upload it and update the field with the URL
      if (form.profile_picture instanceof File) {
        const uploadResult = await uploadMedia(form.profile_picture);
        if (uploadResult?.url) {
          updatedForm.profile_picture = uploadResult.url;
        }
      }

      const response = await updateProfile(updatedForm);
      showSwalNotification(response);
      if (response.status === 'success') {
        onClose();
      }
    } catch (err) {
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      aria-labelledby="update-profile-title"
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: { xs: 1, sm: 2 },
          minWidth: { xs: '90vw', sm: 370, md: 420 },
          maxWidth: { xs: '95vw', sm: 480 },
          width: '100%',
        },
      }}
    >
      <DialogTitle
        id="update-profile-title"
        sx={{
          fontWeight: 'bold',
          color: '#1976d2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: { xs: 18, sm: 22 },
          pb: 1,
        }}
      >
        Update Profile
        <IconButton onClick={onClose} sx={{ ml: 2 }}>
          <FaTimes />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 1, sm: 3 }, py: { xs: 1, sm: 2 } }}>
        <form onSubmit={handleSubmit}>
          <Stack alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Box sx={{ position: 'relative', width: 90, height: 90 }}>
              <Avatar
                src={profilePreview || defaultProfile}
                alt={form.name}
                sx={{
                  width: 90,
                  height: 90,
                  border: '3px solid #43a047',
                  bgcolor: '#e0f2f1',
                  boxShadow: 2,
                }}
              >
                <FaUser size={48} color="#43a047" />
              </Avatar>
              <IconButton
                component="label"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  bgcolor: '#fff',
                  border: '2px solid #43a047',
                  color: '#43a047',
                  '&:hover': { bgcolor: '#e8f5e9' },
                  width: 36,
                  height: 36,
                  zIndex: 1,
                }}
              >
                <FaCamera />
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleProfilePic}
                />
              </IconButton>
            </Box>
          </Stack>
          <Stack spacing={2}>
            <TextField
              label="Full Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              fullWidth
              required
              size="small"
              sx={{ bgcolor: '#f9f9f9', borderRadius: 2 }}
            />
            <TextField
              label="Email"
              name="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              required
              size="small"
              sx={{ bgcolor: '#f9f9f9', borderRadius: 2 }}
              type="email"
            />
            <TextField
              label="Phone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              fullWidth
              size="small"
              sx={{ bgcolor: '#f9f9f9', borderRadius: 2 }}
              type="tel"
            />
            {!!user && user?.role !== 'user' && (
            <TextField
              label="Organisation"
              name="organisation"
              value={form.organisation}
              onChange={handleChange}
              fullWidth
              size="small"
              sx={{ bgcolor: '#f9f9f9', borderRadius: 2 }}
            />
            )}
          </Stack>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <DialogActions sx={{ justifyContent: 'center', mt: 3, gap: 2 }}>
            <Button
              onClick={onClose}
              variant="outlined"
              color="inherit"
              sx={{
                minWidth: 110,
                fontWeight: 'bold',
                borderRadius: 2,
                textTransform: 'none',
                fontSize: { xs: 14, sm: 15 },
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="success"
              sx={{
                minWidth: 140,
                fontWeight: 'bold',
                borderRadius: 2,
                textTransform: 'none',
                fontSize: { xs: 14, sm: 15 },
                boxShadow: 2,
              }}
              disabled={loading}
              startIcon={loading && <CircularProgress size={18} color="inherit" />}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateProfile;